import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import { getCasinoGameDetails, getMatchExposure, getMyBets } from "../../../apiservices/CasionApi";
import "../../../styles/games/card32.css";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import BetTableCard32B from "../../casinoComponents/BetTableCard32B";
import VideoBox from "../../casinoComponents/videoBox";
import Card32CardsOverlay from "../../casinoComponents/videoBox/overlays/Card32CardsOverlay";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import MyBet from "../../casinoComponents/myBet";
import ViewMoreModal from "../../../components/ViewMoreModal";
import ResultCard32B from "../resultui/resultCard32B/ResultCard32B";
import { extractCasinoGame } from "../../../utils/casinoGame";

/**
 * 32 Cards B — route casino/card32eu (reference casino/card32b, .casino-table.cards32b).
 */
export default function Card32B() {
  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "card32eu";
  // res1.cname per feed (the reference header shows it once last results load)
  const [cnames, setCnames] = useState({});
  const title = cnames[gameName] || "32 Cards B";
  const handleTitle = useCallback((cname) => setCnames((prev) => (prev[gameName] === cname ? prev : { ...prev, [gameName]: cname })), [gameName]);

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error(err.message);
      }
    };

    fetchGameData();
    const gameDataInterval = setInterval(fetchGameData, 1500);
    return () => clearInterval(gameDataInterval);
  }, [gameName]);

  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;
  const casinoData = gameData?.data?.data || gameData?.data || gameData || {};
  const matchId = (gameData?.data?.data?.mid ?? gameData?.data?.mid ?? gameData?.mid)?.toString();

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            exposureMap[item.team_name] = parseFloat(item.exposure_amount) || 0;
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMyBets(userId, matchId);
        if (response.success && response.bets) {
          const formattedBets = response.bets.map((bet) => ({
            // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.userName ?? bet.username,
            placeDate: bet.placeDate ?? bet.created_at,
            matchedBet: bet.player_name || bet.selection || "",
            odds: bet.odds || "0",
            stake: bet.stake || "0",
            type: bet.type || null
          }));
          setMyBets(formattedBets);
        }
      }
    } catch (error) {
      console.error('Error fetching my bets:', error);
    }
  }, [matchId]);

  useEffect(() => {
    if (!matchId) return;

    const timeoutId = setTimeout(() => {
      fetchExposure();
      fetchMyBetsData();
    }, 2000);

    const intervalId = setInterval(() => {
      fetchExposure();
      fetchMyBetsData();
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [fetchExposure, fetchMyBetsData, matchId]);

  // Admin panel places no bets: an odds click only refreshes exposure and My Bets
  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  const remark = casinoData?.remark;

  return (
    <CasinoTableLayout
      tableClass="cards32b"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading name={title} roundId={casinoData?.mid ?? ""} />{" "}
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={casinoData?.lt}
          gameType={gameName}
          cardsOverlay={<Card32CardsOverlay cardString={casinoData?.card} variant="b" />}
        />{" "}
        {matchId ? (
          <div className="casino-detail">
            <BetTableCard32B data={casinoData?.sub || []} onBetClick={handleBetClick} exposures={exposures} />{" "}
            {remark ? (
              <div className="casino-remark mt-1">
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} />
                </div>{" "}
                <marquee>{remark}</marquee>
              </div>
            ) : null}{" "}
            <CasinoLastResult
              gameType={gameName}
              resultComponent={<ResultCard32B data={gameData?.lrs || []} onTitle={handleTitle} />}
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
      />
    </CasinoTableLayout>
  );
}
